import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Send,
  ArrowBack,
  CheckCircle,
  CheckCircleOutline,
  Person,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiMessageCircle } from 'react-icons/fi';
import { mqttSupportService } from '../services/mqttSupportService';
import { SupportRequest, ChatMessage } from '../types';
import MultiChannelMessageSender from './MultiChannelMessageSender';
import CannedResponsePicker from './CannedResponsePicker';
import { supportApi as supportApiService } from '../services/supportApi';
import { appendCannedResponseContent, useCannedResponseShortcuts } from '../hooks/useCannedResponseShortcuts';
import './Chat.css';

// Icon wrapper to fix TypeScript compatibility with react-icons
const Icon = ({ icon: IconComponent, style }: { icon: any; style?: React.CSSProperties }) => {
  if (!IconComponent) return null;
  return React.createElement(IconComponent as React.ComponentType<any>, { style });
};

const Chat: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [request, setRequest] = useState<SupportRequest | null>(
    location.state?.request || null
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mqttConnected, setMqttConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [adminId, setAdminId] = useState<number | null>(null);
  const [showMultiChannelSender, setShowMultiChannelSender] = useState(false);
  
  // Canned Response Picker
  const [showCannedResponsePicker, setShowCannedResponsePicker] = useState(false);
  const messageInputRef = useRef<HTMLInputElement>(null);

  useCannedResponseShortcuts(newMessage, setNewMessage);

  useEffect(() => {
    const initialize = async () => {
      if (!id) return;
      
      if (!request) {
        await loadRequest();
      }
      await loadMessages();
    };
    
    initialize();
    setupMQTT();

    return () => {
      if (request?.chatRoomRoomId) {
        mqttSupportService.unsubscribeFromSupportRoom(request.chatRoomRoomId);
      }
    };
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const setupMQTT = () => {
    const token = localStorage.getItem('adminToken');
    mqttSupportService.connect(token || undefined);
    
    setTimeout(() => {
      const connected = mqttSupportService.isConnectedToBroker();
      setMqttConnected(connected);
      
      if (connected && request?.chatRoomRoomId) {
        mqttSupportService.subscribeToSupportRoom(request.chatRoomRoomId, (message) => {
          setMessages((prev) => {
            const exists = prev.some(m => m.id === message.id);
            if (exists) return prev;
            return [...prev, message];
          });
        });
      }
    }, 2000);

    const checkConnection = setInterval(() => {
      setMqttConnected(mqttSupportService.isConnectedToBroker());
    }, 3000);

    return () => clearInterval(checkConnection);
  };

  const loadRequest = async () => {
    if (!id) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      const data = await supportApiService.getRequestById(token, parseInt(id));
      setRequest(data);
      
      if (data.assignedAdminId) {
        setAdminId(data.assignedAdminId);
      } else {
        try {
          const token = localStorage.getItem('adminToken');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            setAdminId(payload.userId || payload.id || payload.sub);
          }
        } catch (e) {
          console.warn('Could not decode admin ID from token');
        }
      }
    } catch (error) {
      console.error('Failed to load request:', error);
    }
  };

  const loadMessages = async () => {
    if (!request?.chatRoomRoomId) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      const response = await fetch(`${supportApiService.getBaseUrl()}/chat/room/${request.chatRoomRoomId}/messages?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load messages');
      }

      const data = await response.json();
      
      const processedMessages = data.map((msg: ChatMessage) => {
        if (adminId) {
          return {
            ...msg,
            isMine: msg.senderId === adminId,
          };
        }
        return msg;
      });
      
      setMessages(processedMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !request || sending) return;

    try {
      setSending(true);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }
      const message = await supportApiService.sendChatMessage(
        token,
        request.requesterId,
        newMessage
      );

      if (!mqttConnected) {
        setMessages((prev) => {
          const exists = prev.some(m => m.id === message.id);
          if (exists) return prev;
          return [...prev, message];
        });
      }

      setNewMessage('');
      setTimeout(() => scrollToBottom(), 100);
    } catch (error: any) {
      console.error('Failed to send message:', error);
      alert(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (!window.confirm('Mark this support request as resolved?')) return;
    if (!id) return;

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }
      await supportApiService.resolveRequest(token, parseInt(id));
      alert('Request marked as resolved');
      navigate('/');
    } catch (error: any) {
      console.error('Failed to resolve request:', error);
      alert(error.message || 'Failed to resolve request');
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInsertCannedResponse = (content: string) => {
    setNewMessage(prev => appendCannedResponseContent(prev, content));
    setTimeout(() => messageInputRef.current?.focus(), 100);
  };

  if (loading || !request) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="chat-container">
      <Box className="chat-header">
        <Box className="header-left">
          <IconButton onClick={() => navigate('/')} sx={{ mr: 2, color: 'var(--text-secondary)' }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'var(--text-primary)' }}>
              {request.subject}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              {request.requesterName} • {request.requesterPhone}
            </Typography>
          </Box>
        </Box>
        <Box className="header-right">
          <Chip
            label={mqttConnected ? 'Connected' : 'Disconnected'}
            color={mqttConnected ? 'success' : 'error'}
            size="small"
            sx={{ mr: 2 }}
          />
          {request.status !== 'RESOLVED' && (
            <Button
              variant="outlined"
              color="success"
              onClick={handleResolve}
              size="small"
            >
              Mark as Resolved
            </Button>
          )}
          {request.status === 'RESOLVED' && (
            <Chip label="Resolved" color="success" size="small" />
          )}
        </Box>
      </Box>

      <Box className="chat-container-single">
        <Box className="chat-main">
          <Box
            ref={messagesContainerRef}
            className="messages-container"
            sx={{ backgroundColor: 'var(--bg-secondary)' }}
          >
            {messages.length === 0 ? (
              <Box className="no-messages">
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                  No messages yet. Start the conversation!
                </Typography>
              </Box>
            ) : (
              messages.map((message) => {
                const isAdminMessage = adminId
                  ? message.senderId === adminId
                  : message.isMine;

                return (
                  <Box
                    key={message.id}
                    className={`message ${isAdminMessage ? 'my-message' : 'other-message'}`}
                  >
                    <Box className="message-header">
                      <Person fontSize="small" sx={{ mr: 1, color: 'var(--text-secondary)' }} />
                      <Typography variant="caption" fontWeight="bold" sx={{ color: 'var(--text-primary)' }}>
                        {message.senderName}
                      </Typography>
                      <Typography variant="caption" sx={{ ml: 1, color: 'var(--text-tertiary)' }}>
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" className="message-content" sx={{ color: 'var(--text-primary)' }}>
                      {message.content}
                    </Typography>
                    {isAdminMessage && message.isRead && (
                      <Box className="read-indicator">
                        {message.isRead ? (
                          <CheckCircle fontSize="small" sx={{ color: 'var(--success-500)' }} />
                        ) : (
                          <CheckCircleOutline fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box className="message-input-container" sx={{ backgroundColor: 'var(--bg-elevated)', borderTop: '1px solid var(--border-light)' }}>
            {/* Canned Response Button */}
            <Tooltip title="Insert canned response (Ctrl+Shift+C)">
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowCannedResponsePicker(true)}
                sx={{
                  mr: 1,
                  minWidth: 'auto',
                  px: 1.5,
                  borderColor: 'var(--border-light)',
                  color: 'var(--text-secondary)',
                  '&:hover': {
                    borderColor: 'var(--primary-500)',
                    color: 'var(--primary-500)',
                  },
                }}
              >
                <MessageIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>

            <Button
              variant="contained"
              color="success"
              onClick={() => setShowMultiChannelSender(true)}
              sx={{ mr: 1, minWidth: '140px', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Icon icon={FiMessageCircle} style={{ fontSize: '18px' }} />
              Multi-Channel
            </Button>
            <TextField
              fullWidth
              placeholder="EN: /greet  |  AR: /greet_ar  — or type freely"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              size="small"
              inputRef={messageInputRef}
              sx={{ mr: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
              startIcon={sending ? <CircularProgress size={16} /> : <Send />}
              sx={{
                backgroundColor: 'var(--primary-600)',
                '&:hover': { backgroundColor: 'var(--primary-700)' },
              }}
            >
              Send
            </Button>
          </Box>

          {/* Canned Response Picker */}
          <CannedResponsePicker
            anchorEl={messageInputRef.current}
            open={showCannedResponsePicker}
            onClose={() => setShowCannedResponsePicker(false)}
            onSelect={handleInsertCannedResponse}
            placement="top"
          />

          {/* Multi-Channel Message Sender Modal */}
          {showMultiChannelSender && request && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
                padding: 2,
              }}
              onClick={() => setShowMultiChannelSender(false)}
            >
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ maxWidth: '600px', width: '100%' }}
              >
                <MultiChannelMessageSender
                  userId={request.requesterId}
                  userName={request.requesterName}
                  userEmail={request.requesterEmail}
                  userPhone={request.requesterPhone}
                  onClose={() => setShowMultiChannelSender(false)}
                  onSent={(channel) => {
                    console.log(`Message sent via ${channel}`);
                  }}
                />
              </Box>
            </Box>
          )}

          {!mqttConnected && (
            <Box className="connection-warning">
              <Typography variant="caption" sx={{ color: 'var(--warning-500)' }}>
                Real-time updates unavailable. Messages will be sent via HTTP.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Chat;
